import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import "./Auth.css";

function Auth() {
  const { user, signOut, getUsername } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");
    setLoading(true);
    const result = isSignUp
      ? await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: window.location.origin + "/auth" },
        })
      : await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (result.error) setError(result.error.message);
    else if (isSignUp) setMessage("Check your email for confirmation link.");
  };

  const oauthSignIn = (provider) => {
    supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: window.location.origin + "/auth" },
    });
  };

  if (user) {
    return (
      <>
        <Helmet>
          <title>My Account | AxiomLabs Solutions</title>
          <meta
            name="description"
            content="Manage your AxiomLabs account, view your licenses, and access your purchases."
          />
        </Helmet>
        <div className="auth-page">
          <div className="auth auth--logged-in">
            <div className="auth__avatar">
              {getUsername(user.email).charAt(0).toUpperCase()}
            </div>
            <h1 className="auth__title">{getUsername(user.email)}</h1>
            <p className="auth__subtitle">{user.email}</p>
            <div className="auth__info">
              <div className="auth__info-row">
                <span>Email verified</span>
                <span className="auth__check">✓</span>
              </div>
            </div>
            <button
              className="auth__btn"
              onClick={() => {
                signOut();
              }}
            >
              Sign Out
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          {isSignUp ? "Create Account" : "Sign In"} | AxiomLabs Solutions
        </title>
        <meta
          name="description"
          content="Sign in to access your purchases, licenses, and downloads from AxiomLabs Solutions."
        />
      </Helmet>
      <div className="auth-page">
        <div className="auth">
          <svg
            className="auth__logo"
            width="48"
            height="48"
            viewBox="0 0 64 64"
            fill="none"
          >
            <rect x="8" y="8" width="20" height="20" rx="4" fill="#0D9488" />
            <rect x="36" y="8" width="20" height="20" rx="4" fill="#0D9488" />
            <rect x="8" y="36" width="20" height="20" rx="4" fill="#0D9488" />
            <rect x="36" y="36" width="20" height="20" rx="4" fill="#0D9488" />
          </svg>
          <h1 className="auth__title">
            {isSignUp ? "Create Account" : "Sign In"}
          </h1>
          <p className="auth__subtitle">
            {isSignUp
              ? "Create an account to manage your licenses."
              : "Sign in to access your purchases and licenses."}
          </p>
          <div className="auth__providers">
            <button
              className="auth__provider-btn"
              onClick={() => oauthSignIn("google")}
            >
              <svg width="18" height="18" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Google
            </button>
            <button
              className="auth__provider-btn"
              onClick={() => oauthSignIn("github")}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M12 0C5.37 0 0 5.37 0 12c0 5.3 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61-.546-1.385-1.335-1.755-1.335-1.755-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 21.795 24 17.295 24 12c0-6.63-5.37-12-12-12z" />
              </svg>
              GitHub
            </button>
          </div>
          <div className="auth__divider">
            <span>or use email</span>
          </div>
          <form onSubmit={handleSubmit} className="auth__form">
            <input
              className="auth__input"
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <input
              className="auth__input"
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
            />
            <button className="auth__btn" type="submit" disabled={loading}>
              {loading
                ? "Please wait..."
                : isSignUp
                  ? "Create Account"
                  : "Sign In"}
            </button>
          </form>
          {message && <p className="auth__message">{message}</p>}
          {error && <p className="auth__error">{error}</p>}
          <p className="auth__toggle">
            {isSignUp ? "Already have an account? " : "Don't have an account? "}
            <button
              className="auth__toggle-btn"
              onClick={() => {
                setIsSignUp(!isSignUp);
                setMessage("");
                setError("");
              }}
            >
              {isSignUp ? "Sign In" : "Sign Up"}
            </button>
          </p>
        </div>
      </div>
    </>
  );
}

export default Auth;
